/**
 * Instance Advice top-bar rules — the contract discount check, the download
 * file names, and what Regenerate sends. Pure, so each legacy rule is tested
 * on its own (legacy views/CostAdvice/SpecCpuCostAdvice/exportedFile.vue).
 */
import {
  emptyContractDiscount,
  fromPricingModelDiscount,
} from '@components/common/ContractDiscountDialog/contractDiscountRules';

/** The three views (legacy selectedViewMode 0 / 1 / 2). Analytics first. */
export const ADVICE_VIEWS = Object.freeze({
  ANALYTICS: 'analytics',
  CARD: 'card',
  TABLE: 'table',
});

/** Savings Type "All" (legacy savingsType default). */
export const SAVINGS_ALL = 'All';

/**
 * Savings Type options per product (legacy HeaderActions.vue). EIA shows two
 * recommendations, CCA three paths.
 */
export const SAVINGS_TYPES = Object.freeze({
  eia: Object.freeze([SAVINGS_ALL, 'Optimal', 'Good']),
  cca: Object.freeze([
    SAVINGS_ALL,
    'Hourly Cost Optimization',
    'Modernize',
    'Modernize & Downsize',
  ]),
});

/**
 * Why a typed contract discount cannot be applied, or null. Legacy's
 * messages, verbatim (confirmMsaEdit): unlike Manage Portfolio's field, an
 * empty value is refused here — the check icon means "apply this number".
 */
export function discountError(value) {
  const raw = String(value ?? '').trim();
  const n = Number.parseFloat(raw);
  if (raw === '' || Number.isNaN(n) || !Number.isFinite(n)) {
    return 'Contract Discount%: Must be a number';
  }
  if (n < 0) return 'Contract Discount%: Must be 0 or greater';
  if (n > 99.9) return 'Contract Discount%: Must be 99.90 or less';
  if (/\.(\d{3,})$/.test(raw)) return 'Contract Discount%: Max 2 decimal places allowed';
  return null;
}

/**
 * The discount the advice was worked out with, as the UI value
 * ({ onDemand, oneYearRi, threeYearRi }): the server's answer
 * (grandTotal.pricing_model_discount, which replaced msa_discount on
 * 2026-10-21) once there is one, else what was handed over, else the
 * portfolio's saved one, else all blank (was 0) — legacy's watchers, in order.
 */
export function appliedDiscount({ advice, handedOver, portfolio }) {
  return (
    fromPricingModelDiscount(advice?.grandTotal?.pricing_model_discount) ??
    handedOver ??
    fromPricingModelDiscount(portfolio?.pricing_model_discount) ??
    emptyContractDiscount()
  );
}

/**
 * The name a downloaded file is saved under: the last part of its address
 * (without the query string), given the right extension — or the fallback
 * when the address has no file name (legacy DownloadPPT / DownloadExcel).
 */
export function downloadFileName(url, { fallback, extension }) {
  let name = '';
  try {
    name = decodeURIComponent(new URL(url).pathname.split('/').pop() ?? '');
  } catch {
    name =
      String(url ?? '')
        .split('/')
        .pop()
        ?.split('?')[0] ?? '';
  }
  if (!name || !name.includes('.')) return fallback;
  return name.endsWith(extension) ? name : `${name}${extension}`;
}
